import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { getErrorMessage } from '../../constants/errorCodes';

function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 4) {
      setError(getErrorMessage(t, 'RESET_PASSWORD_WEAK'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('The passwords do not match.'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchWithAuth('auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      let data = {};
      try { data = await response.json(); } catch (_) {}

      if (!response.ok) {
        const code = data.errorCode || 'DEFAULT';
        setError(getErrorMessage(t, code));
        return;
      }

      setSuccess(true);
    } catch {
      setError(t('An error occurred. Please try again later.'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-2">{t('Invalid link')}</h2>
          <p className="text-gray-600 mb-4">{t('Invalid or expired link. Please request a new one.')}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md"
          >
            {t('Back to sign in')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-2">{t('Password reset')}</h2>
          <p className="text-gray-600 mb-4">{t('Your password has been reset successfully.')}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md"
          >
            {t('Sign in')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-2">{t('New password')}</h2>
        <p className="text-gray-600 mb-4">{t('Enter your new password.')}</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('New password')}
            className="w-full border rounded-md px-3 py-2 mb-3"
            disabled={isLoading}
            autoFocus
          />
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('Confirm the password')}
            className="w-full border rounded-md px-3 py-2 mb-3"
            disabled={isLoading}
          />

          {error && (
            <div className="text-red-600 text-sm mb-3">{error}</div>
          )}

          <button
            type="submit"
            className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md disabled:opacity-60"
            disabled={isLoading || !password || !confirmPassword}
          >
            {isLoading ? t('Updating…') : t('Validate')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
